// Vendor Libs
import { useDispatch, useSelector } from 'react-redux';
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {get, map, noop, remove, startCase, findIndex} from 'lodash';

// Components
import {Checkbox} from 'primereact/checkbox';
import Button from '../core/button';
import Form from '../core/form';
import Input from '../core/input';
import LanguageSelect from './language-select';
import Loading from '../core/loading';
import Permissions from '../core/permissions';
import Select from '../core/select';
import SubmitButton from '../core/submit-button';
import Textarea from '../core/textarea';
import DateTime from '../core/date-time';
import GenreSelect from './genre-select';
import FormData from '../../containers/form-data';
import Thumbnail from '../core/thumbnail';
import ImageUploader from './image-uploader';
import ShellCredits from './shell-credits';

// Data
import echostarContentTypeMap from '../../bootstrap-data/echostar-content-type-map';

// Styles
import './shell-form.scss';
import GraceNoteControlsGroup from './send-to-gracenote';

// Redux Actions
import {standardizeGenres} from '../../actions/standardize-genres-actions';

function differenceAsString(a, b) {
  if (!a?.length || !b?.length) return '';

  const setB = new Set(b);
  return a.filter(item => !setB.has(item)).join(', ');
}

function ShellForm({
  bulkMapping,
  editing,
  episodeOnly,
  formData: { values: formValues = {} },
  formName,
  massShellActive,
  onReset,
  searchFetching,
  setFieldValue,
  setFormSubmitted,
}) {
  const [isExtra, setIsExtra] = useState(false);
  const [currGenres, setCurrGenres] = useState([]);
  const servicesSearchData = useSelector(state => state.servicesSearchData);
  const searchData = servicesSearchData?.genresSelect?.searchData?.standardized_genres;
  const dispatch = useDispatch();

  const unknownGenre = differenceAsString(currGenres, searchData);

  const shellLabel = unknownGenre ? `Genre (has unknown Genres: ${unknownGenre})` : 'Genre';

  const areArraysEqual = (a, b) =>
    a.length === b.length &&
    a.every((value, index) => value === b[index]);

  useEffect(() => {
    if (formValues && Object.keys(formValues).length) {
      const { extra, genres } = formValues;
      setIsExtra(extra !== null && extra !== undefined ? extra : false);

    if ((genres != null) && !areArraysEqual(genres, currGenres) && !Config.base.matureContent) {
      setCurrGenres(genres);
  }
    }
  }, [formValues]);

  useEffect(() => {
    if (Config.base.matureContent || (!currGenres?.length)) return;

    if (searchData && searchData.length > 0) {
      if (!currGenres.every(g => searchData.includes(g))) {
        dispatch(standardizeGenres(currGenres, formName));
      }
    } else {
      dispatch(standardizeGenres(currGenres, formName));
    }
  }, [currGenres, dispatch]);

  const getFormProps = () => ({
    className: !editing ? 'shell-form--no-button' : 'shell-form',
    fixedPositionedResponse: true,
    name: formName,
    onSubmit: handleSubmit,
    resetOnSubmit: !editing,
    submitData: editing,
  });

  const getFieldProps = (fieldName, fieldType) => ({
    className: fieldType === 'select' ? 'shell-form--select' : 'shell-form--input',
    label: startCase(fieldName),
    name: fieldName,
  });

  const handleSubmit = () => {
    editing ? noop() : setFormSubmitted(false, true);
  };

  const handleResetClick = (event = { preventDefault: noop }) => {
    event && event.preventDefault && event.preventDefault();

    onReset();
  };

  const isContentTypeDisabled = (type) => (episodeOnly && type !== 'SRE')
        || (!episodeOnly && type === 'SRE')
        || (massShellActive && type === 'SRM')
        || (bulkMapping && type !== 'SRM');

  const renderResetButton = () => (editing ? (
            <Button className="shell-form--reset-button" onClick={handleResetClick}>
                Reset
            </Button>
        ) : null);

  const renderGraceNoteButton = () => (editing ? <GraceNoteControlsGroup isMoviesAndPrograms={true} /> : null);

  const renderContentType = () => (
        <div className="shell-form--content-type">
            Content Type: {get(echostarContentTypeMap, [formValues.echostarContentType, 'name'])}
        </div>
  );

  const renderContentTypeSelect = () => (
        <Select {...getFieldProps('echostarContentType', 'select')} label="Content Type" validate={['required']}>
            <option value="">Select Content Type</option>
            {map(echostarContentTypeMap, ({ name }, type) => (
                <option disabled={isContentTypeDisabled(type)} key={type} value={type}>
                    {name}
                </option>
            ))}
        </Select>
  );

  const renderEpisodeFields = () => {
    let content = null;

    if (get(formValues, 'echostarContentType') === 'SRE') {
      content = (
                <span>
                    <Input {...getFieldProps('episodeSeason')} label="Season Number" validate={['numeric']} />
                    <Input {...getFieldProps('episodeNumber')} validate={['numeric']} />
                </span>
      );
    }

    return content;
  };

  const renderSaveButton = () => (editing ? (
            <Permissions accessRequirement="write">
                <SubmitButton className="shell-form--submit-button" label="Save" />
            </Permissions>
        ) : null);

  const renderSeriesTitle = () => {
    let content = null;

    if (episodeOnly) {
      content = (
                <>
                    <Input label="Series Title" name="seriesTitle" inputType="hidden" />
                    <div className="shell-form--series-title">{formValues.seriesTitle}</div>
                </>
      );
    }

    return content;
  };

  const aspectRatio2_1 = 2;
  const aspectRatio3_4 = 0.75;
  const aspectRatio16_9 = 1.777777777777778;
  const aspectRatio4_3 = 1.333333333333333;
  const aspectRatio13_5 = 2.6;

  const imageTypes = [
    { type: 'Franchise', description: 'Franchise 4:3', aspectRatio: '4:3', aspectRatioCalculated: aspectRatio4_3 },
    {
      type: 'Series2x1',
      description: 'Series Images 2:1',
      aspectRatio: '2:1',
      aspectRatioCalculated: aspectRatio2_1,
    },
    {
      type: 'Movie2x1',
      description: 'Movie Images 2:1',
      aspectRatio: '2:1',
      aspectRatioCalculated: aspectRatio2_1,
    },
    {
      type: 'Series3x4',
      description: 'Series Images 3:4',
      aspectRatio: '3:4',
      aspectRatioCalculated: aspectRatio3_4,
    },
    {
      type: 'Movie3x4',
      description: 'Movie Images 3:4',
      aspectRatio: '3:4',
      aspectRatioCalculated: aspectRatio3_4,
    },
    {
      type: 'Series16x9',
      description: 'Series Widescreen 16:9',
      aspectRatio: '16:9',
      aspectRatioCalculated: aspectRatio16_9,
    },
    {
      type: 'Movie16x9',
      description: 'Movie Widescreen 16:9',
      aspectRatio: '16:9',
      aspectRatioCalculated: aspectRatio16_9,
    },
    {
      type: 'SeriesBackground16x9',
      description: 'Series Background 16:9',
      aspectRatio: '16:9',
      aspectRatioCalculated: aspectRatio16_9,
    },
    {
      type: 'MovieBackground16x9',
      description: 'Movie Background 16:9',
      aspectRatio: '16:9',
      aspectRatioCalculated: aspectRatio16_9,
    },
    {
      type: 'Episode',
      description: 'Episode',
      aspectRatio: '4:3',
      aspectRatioCalculated: aspectRatio4_3,
    },
    {
      type: 'EpisodeBackground16x9',
      description: 'Episode Background 16:9',
      aspectRatio: '16:9',
      aspectRatioCalculated: aspectRatio16_9,
    },
    {
      type: 'Special16x9',
      description: 'Special 16:9',
      aspectRatio: '16:9',
      aspectRatioCalculated: aspectRatio16_9,
    },
    {
      type: 'SpecialBackground16x9',
      description: 'Special Background 16:9',
      aspectRatio: '16:9',
      aspectRatioCalculated: aspectRatio16_9,
    },
    {
      type: 'TitleArt',
      description: 'Title Art',
      aspectRatio: '13:5',
      aspectRatioCalculated: aspectRatio13_5,
    },
  ];

  const ratings = [
    { rating: 'G', description: 'G' },
    { rating: 'NC17', description: 'NC17' },
    { rating: 'Not Rated', description: 'Not Rated' },
    { rating: 'NR', description: 'NR' },
    { rating: 'NR/AO', description: 'NR/AO' },
    { rating: 'PG', description: 'PG' },
    { rating: 'PG13', description: 'PG13' },
    { rating: 'R', description: 'R' },
    { rating: 'TV14', description: 'TV14' },
    { rating: 'TVG', description: 'TVG' },
    { rating: 'TVMA', description: 'TVMA' },
    { rating: 'TVPG', description: 'TVPG' },
    { rating: 'TVY', description: 'TVY' },
    { rating: 'TVY7', description: 'TVY7' },
    { rating: 'X', description: 'X' },
  ];

  const getShellImageCategory = (formData) => get(formData, 'values.shellImageCategory', '');

  const getCurrentShellImage = (formData) => (getImageUrl(formData) === '' ? null : <Thumbnail key="" src={getImageUrl(formData)} />);

  const getImageUrl = (formData) => (getCurrentImageIndex(formData) !== -1 ? getImages(formData)[getCurrentImageIndex(formData)].url : '');

  const getImageDimensions = (url) => new Promise((resolve, reject) => {
      if (url && url.length) {
        const img = new Image();

        // the following handler will fire after a successful loading of the image
        img.onload = () => {
          const { naturalWidth: width, naturalHeight: height } = img;
          resolve({ width, height });
        };

        // and this handler will fire if there was an error with the image (like if it's not really an image or a corrupted one)
                img.onerror = () => {
          reject('There was some problem with the image.');
        };
        img.src = url;
      } else {
                resolve(null);
            }
    });

  const handleUploaderChange = async (formData, imageUrl) => {
    const category = getShellImageCategory(formData);
    const type = imageTypes.find((image) => image.type === category);
    const imagesMod = remove(Object.assign([], getImages(formData)), (image) => image.category !== category);
    const dimensions = await getImageDimensions(imageUrl);
    if (dimensions && dimensions.width) {
      imagesMod.push({
        category: category,
        url: imageUrl || '',
        imageWidth: dimensions.width,
        imageHeight: dimensions.width / type?.aspectRatioCalculated,
        aspectRatio: type?.aspectRatio || '',
        echostarImageId: 0,
      });
      setFieldValue('images', imagesMod);
    }
  };

  const getCurrentImageIndex = (formData) => findIndex(getImages(formData), (image) => image.category === getShellImageCategory(formData));

  const getImages = (formData) => get(formData, 'values.images', []);

  const getModifiedShellCategory = (formData) => {
    const ShellCategory = getShellImageCategory(formData);
    const type = imageTypes.find((image) => image.type === ShellCategory);
    return type?.description || startCase(ShellCategory);
  };

  const removeCurrentImage = (formData) => {
    const imagesMod = remove(
      Object.assign([], getImages(formData)),
      (image) => image.category !== getShellImageCategory(formData),
    );
    setFieldValue('images', imagesMod);
  };

  const renderUploader = () => (
        <>
            <Select {...getFieldProps('shellImageCategory', 'select')}>
                <option value="">Choose Shell Image</option>
                {imageTypes.map((imageType, key) => (
                    <option key={key} value={imageType.type}>
                        {imageType.description}
                    </option>
                ))}
            </Select>
            <FormData formName={formName}>
                {({ formData }) => (
                    <>
                        {getShellImageCategory(formData) === '' ? null : (
                            <div>
                                <ImageUploader
                                    autoUpload={false}
                                    currentImage={getCurrentShellImage(formData)}
                                    formData={formData}
                                    onChange={(imageUrl) => handleUploaderChange(formData, imageUrl)}
                                />
                                {getCurrentImageIndex(formData) !== -1 ? (
                                    <>
                                        <h5 className="shell-form--image-url-label">
                                            {getModifiedShellCategory(formData)} Image URL
                                        </h5>
                                        <p className="shell-form--image-url">
                                            {getImages(formData)[getCurrentImageIndex(formData)].url}
                                        </p>
                                        <Button
                                            className="shell-form--unmap-shell-image-button"
                                            onClick={() => removeCurrentImage(formData)}
                                        >
                                            Remove {getModifiedShellCategory(formData)} Image
                                        </Button>
                                    </>
                                ) : null}
                            </div>
                        )}
                    </>
                )}
            </FormData>
        </>
  );

  const renderRating = () => (
        <Select {...getFieldProps('rating', 'select')} validate={['rating']}>
            <option>Select a rating...</option>
            {ratings.map((rating, key) => (
                <option key={key} value={rating.rating}>
                    {rating.description}
                </option>
            ))}
        </Select>
  );

  const renderShellExtras = () => (
        <div className="p-field-checkbox shell-form--shell-extras">
            <Checkbox
                checked={isExtra}
                onChange={() => {
                  setFieldValue('extra', !isExtra);
                  setIsExtra(!isExtra);
                }}
            />
            <label>Shell Extras</label>
        </div>
  );

  return (
        <Form {...getFormProps()}>
            {editing ? renderContentType() : renderContentTypeSelect()}
            {renderSeriesTitle()}
            <Input {...getFieldProps('title')} validate={['required']} />
            <Textarea {...getFieldProps('description')} />
            <Textarea {...getFieldProps('shortDescription')} />
            <DateTime {...getFieldProps('originalAirDate')} parseFormat="YYYY-MM-DD" />
            <Input {...getFieldProps('releaseYear')} validate={['number', 'required', 'releaseYear']} />
            <Input {...getFieldProps('runtime')} validate={['duration']} />
            <LanguageSelect {...getFieldProps('titleLanguages', 'select')} noMulti={true} />
            <LanguageSelect {...getFieldProps('descriptionLanguage', 'select')} noMulti={true} />
            <GenreSelect name="genres" label={shellLabel} value={[]} />

            {renderEpisodeFields()}
            {renderRating()}
            {renderUploader()}
            {renderShellExtras()}
            <ShellCredits formValues={formValues} setFieldValue={setFieldValue} />
            {renderSaveButton()}
            {renderResetButton()}
            {renderGraceNoteButton()}
            <Loading loading={searchFetching} />
        </Form>
  );
}

ShellForm.propTypes = {
    bulkMapping: PropTypes.bool,
    editing: PropTypes.bool,
    episodeOnly: PropTypes.bool,
    formData: PropTypes.object.isRequired,
    formName: PropTypes.string.isRequired,
    massShellActive: PropTypes.bool,
    onReset: PropTypes.func,
    searchFetching: PropTypes.bool,
    setFieldValue: PropTypes.func,
    setFormSubmitted: PropTypes.func,
};

export default ShellForm;
